import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BadgePercent, CalendarDays, PawPrint, Send, Users, Waves } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ChapterHeading from "@/components/landing/ChapterHeading";
import { toKey, parseKey, fmtDate, nightsBetween } from "@/lib/dates";

const inputCls =
  "w-full rounded-sm border border-stone-300 bg-linen px-4 py-3 text-sm text-stone-800 outline-none transition-colors duration-200 placeholder:text-stone-400 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

const PRICE = { weekday: 75, weekend: 84, spaDay: 40, petSmall: 5, petOther: 10 };
const euro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
const fmtShort = (d) => new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", month: "short" }).format(d);

const pillCls = (on) =>
  `rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-200 ${
    on ? "border-terracotta bg-terracotta text-linen" : "border-stone-300 text-stone-600 hover:border-terracotta hover:text-terracotta"
  }`;

export default function Prenota({ range, onSelect }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 2, notes: "" });
  const [rateType, setRateType] = useState("flexible");
  const [spaDays, setSpaDays] = useState(new Set());
  const [pets, setPets] = useState("none");
  const [sending, setSending] = useState(false);

  const nights = nightsBetween(range.from, range.to);
  const todayKey = toKey(new Date());

  const stayDays = useMemo(() => {
    if (!range.from || !range.to) return [];
    const days = [];
    for (let d = new Date(range.from); d < range.to; d.setDate(d.getDate() + 1)) days.push(new Date(d));
    return days;
  }, [range.from, range.to]);

  const nonrefEligible = !!range.from && (range.from - new Date()) / 86400000 >= 15;

  useEffect(() => {
    const valid = new Set(stayDays.map(toKey));
    setSpaDays((prev) => new Set([...prev].filter((k) => valid.has(k))));
  }, [stayDays]);

  useEffect(() => {
    if (!nonrefEligible) setRateType("flexible");
  }, [nonrefEligible]);

  const pricing = useMemo(() => {
    const room = stayDays.reduce((s, d) => s + ([5, 6].includes(d.getDay()) ? PRICE.weekend : PRICE.weekday), 0);
    const spa = spaDays.size * PRICE.spaDay;
    const pet = pets === "small" ? PRICE.petSmall * nights : pets === "other" ? PRICE.petOther * nights : 0;
    const discount = rateType === "nonrefundable" ? Math.round(room * 0.2 * 100) / 100 : 0;
    return { room, spa, pet, discount, total: room + spa + pet - discount };
  }, [stayDays, spaDays, pets, rateType, nights]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleSpaDay = (k) =>
    setSpaDays((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const setDate = (which) => (e) => {
    const d = e.target.value ? parseKey(e.target.value) : null;
    onSelect(d, which === "from");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!range.from || !range.to) {
      toast.error("Seleziona prima le date di check-in e check-out dal calendario.");
      return;
    }
    setSending(true);
    try {
      await api.post("/bookings", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        guests: Number(form.guests),
        checkin: toKey(range.from),
        checkout: toKey(range.to),
        notes: form.notes.trim(),
        rate_type: rateType,
        spa_days: [...spaDays].sort(),
        pets,
      });
      toast.success(`Richiesta inviata! Totale stimato: ${euro(pricing.total)}. Alessio ti risponderà al più presto.`);
      setForm({ name: "", email: "", phone: "", guests: 2, notes: "" });
      setRateType("flexible");
      setSpaDays(new Set());
      setPets("none");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Qualcosa è andato storto. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const hasDates = range.from && range.to;

  return (
    <section id="prenota" data-testid="booking-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
      <ChapterHeading
        eyebrow="Capitolo 05"
        title="Prenota il tuo soggiorno"
        subtitle="Ven e sab 84€ a notte, gli altri giorni 75€. Sconto del 20% con tariffa non rimborsabile. Nessuna commissione: la richiesta va diretta all'host."
      />

      <div className="grid gap-10 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="rounded-sm bg-olive p-8 text-linen lg:col-span-4 sm:p-10"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-linen/60">Il tuo soggiorno</p>
          <div className="mt-6 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-linen/50">Check-in</p>
              <p className="mt-1 font-serif text-2xl" data-testid="summary-checkin">
                {range.from ? fmtDate(range.from) : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-linen/50">Check-out</p>
              <p className="mt-1 font-serif text-2xl" data-testid="summary-checkout">
                {range.to ? fmtDate(range.to) : "—"}
              </p>
            </div>
            <div className="border-t border-linen/15 pt-5">
              <p className="font-serif text-4xl text-oro" data-testid="summary-nights">
                {nights} <span className="text-lg text-linen/70">{nights === 1 ? "notte" : "notti"}</span>
              </p>
            </div>
          </div>

          {hasDates && (
            <div className="mt-6 space-y-2 border-t border-linen/15 pt-5 text-sm" data-testid="price-breakdown">
              <div className="flex justify-between gap-4">
                <span className="font-light text-linen/80">Soggiorno · {nights} {nights === 1 ? "notte" : "notti"}</span>
                <span data-testid="price-room">{euro(pricing.room)}</span>
              </div>
              {pricing.spa > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="font-light text-linen/80">Piscina SPA · {spaDays.size} {spaDays.size === 1 ? "giorno" : "giorni"}</span>
                  <span data-testid="price-spa">{euro(pricing.spa)}</span>
                </div>
              )}
              {pricing.pet > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="font-light text-linen/80">Animali domestici</span>
                  <span data-testid="price-pets">{euro(pricing.pet)}</span>
                </div>
              )}
              {pricing.discount > 0 && (
                <div className="flex justify-between gap-4 text-oro">
                  <span className="font-light">Sconto non rimborsabile −20%</span>
                  <span data-testid="price-discount">−{euro(pricing.discount)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-linen/15 pt-3">
                <span className="text-xs uppercase tracking-[0.15em] text-linen/60">Totale</span>
                <span className="font-serif text-4xl text-oro" data-testid="price-total">{euro(pricing.total)}</span>
              </div>
            </div>
          )}

          <p className="mt-8 flex items-start gap-2 text-sm font-light leading-relaxed text-linen/70">
            <CalendarDays size={16} className="mt-0.5 shrink-0 text-oro" />
            Scegli le date dal calendario qui sopra oppure inseriscile nei campi del modulo.
          </p>
          <p className="mt-3 text-xs text-linen/50">Ven e sab 84€/notte · altri giorni 75€/notte · SPA 40€/giorno</p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="booking-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-sm border border-stone-300/50 bg-white p-8 shadow-xl shadow-stone-900/5 lg:col-span-8 sm:p-10"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Check-in</label>
              <input data-testid="booking-checkin-input" type="date" min={todayKey} value={range.from ? toKey(range.from) : ""} onChange={setDate("from")} className={inputCls} required />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Check-out</label>
              <input data-testid="booking-checkout-input" type="date" min={range.from ? toKey(range.from) : todayKey} value={range.to ? toKey(range.to) : ""} onChange={setDate("to")} className={inputCls} required />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Nome e cognome</label>
              <input data-testid="booking-name-input" type="text" value={form.name} onChange={set("name")} placeholder="Maria Rossi" className={inputCls} required minLength={2} />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Email</label>
              <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="maria@esempio.it" className={inputCls} required />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Telefono</label>
              <input data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} required />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Ospiti</label>
              <div className="relative">
                <Users size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <select data-testid="booking-guests-select" value={form.guests} onChange={set("guests")} className={`${inputCls} appearance-none pl-10`}>
                  <option value={1}>1 ospite</option>
                  <option value={2}>2 ospiti</option>
                </select>
              </div>
              <p className="mt-1.5 text-xs text-stone-400">Lettino e cancelletti per bambini su richiesta</p>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-stone-500">
                <BadgePercent size={14} className="text-terracotta" /> Tariffa
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  data-testid="rate-flexible"
                  onClick={() => setRateType("flexible")}
                  className={`rounded-sm border p-4 text-left transition-colors duration-200 ${
                    rateType === "flexible" ? "border-terracotta bg-terracotta/5 ring-2 ring-terracotta/20" : "border-stone-300 hover:border-terracotta"
                  }`}
                >
                  <p className="text-sm font-semibold text-stone-800">Flessibile</p>
                  <p className="mt-1 text-xs font-light text-stone-500">Cancellazione libera: concordi con l'host in caso di imprevisti</p>
                </button>
                <button
                  type="button"
                  data-testid="rate-nonrefundable"
                  onClick={() => nonrefEligible && setRateType("nonrefundable")}
                  disabled={!nonrefEligible}
                  className={`rounded-sm border p-4 text-left transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
                    rateType === "nonrefundable" ? "border-terracotta bg-terracotta/5 ring-2 ring-terracotta/20" : "border-stone-300 hover:border-terracotta"
                  }`}
                >
                  <p className="text-sm font-semibold text-stone-800">Non rimborsabile · −20%</p>
                  <p className="mt-1 text-xs font-light text-stone-500">
                    {nonrefEligible ? "Sconto del 20% sul soggiorno, senza possibilità di rimborso" : "Prenotabile solo almeno 15 giorni prima del check-in"}
                  </p>
                </button>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-stone-500">
                <Waves size={14} className="text-terracotta" /> Piscina SPA gonfiabile riscaldata · 40€/giorno
              </label>
              {!hasDates ? (
                <p className="rounded-sm bg-linen p-3 text-xs text-stone-500" data-testid="spa-hint">
                  Scegli prima le date del soggiorno: poi potrai aggiungere la SPA solo per i giorni che vuoi.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="spa-days">
                  {stayDays.map((d) => {
                    const k = toKey(d);
                    return (
                      <button key={k} type="button" data-testid={`spa-day-${k}`} onClick={() => toggleSpaDay(k)} className={pillCls(spaDays.has(k))}>
                        {fmtShort(d)}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="mt-1.5 text-xs text-stone-400">Si prenota separatamente: puoi stare 3 notti e usare la SPA anche un solo giorno</p>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-stone-500">
                <PawPrint size={14} className="text-terracotta" /> Animali domestici
              </label>
              <div className="flex flex-wrap gap-2" data-testid="pets-options">
                <button type="button" data-testid="pets-none" onClick={() => setPets("none")} className={pillCls(pets === "none")}>Nessuno</button>
                <button type="button" data-testid="pets-small" onClick={() => setPets("small")} className={pillCls(pets === "small")}>Piccola taglia · max 5 kg · +5€/giorno</button>
                <button type="button" data-testid="pets-other" onClick={() => setPets("other")} className={pillCls(pets === "other")}>Altre taglie · +10€/giorno</button>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Note speciali</label>
              <textarea data-testid="booking-notes-input" value={form.notes} onChange={set("notes")} rows={3} placeholder="Orari di arrivo, richieste particolari…" className={`${inputCls} resize-none`} />
            </div>
          </div>

          <button
            data-testid="reservation-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-linen transition-transform duration-300 hover:scale-[1.02] hover:bg-[#9C5B41] active:scale-95 disabled:opacity-50 sm:w-auto"
          >
            {sending ? "Invio in corso…" : "Invia richiesta di prenotazione"}
            <Send size={15} />
          </button>
          <p className="mt-4 text-xs text-stone-400">
            Nessun pagamento ora: la richiesta viene inviata all'host, che conferma la disponibilità personalmente.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
